import type { UserRole } from '@/common/db-typebox';
import { useAuth } from '@/stores/AuthContext';
import { observer } from 'mobx-react-lite';
import type { LinkProps } from 'react-router-dom';
import { Link, Navigate, Outlet } from 'react-router-dom';

// Keep your PermissionLink component as is
interface PermissionLinkProps extends LinkProps {
  permissions?: UserRole[];
  fallback?: React.ReactNode;
}

export const PermissionLink = observer(
  ({
    permissions,
    fallback = null,
    children,
    ...props
  }: PermissionLinkProps) => {
    const { user } = useAuth();

    if (!permissions || permissions?.length === 0) {
      return <Link {...props}>{children}</Link>;
    }

    if (user && permissions.includes(user.role)) {
      return <Link {...props}>{children}</Link>;
    }

    return fallback;
  },
);

// Create a component to wrap protected content
interface RequireAuthProps {
  permissions?: UserRole[];
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

// This component can be used directly in your route element prop
export const RequireAuth = observer(
  ({ permissions, fallback, children }: RequireAuthProps) => {
    const { user } = useAuth();

    if (!permissions || permissions?.length === 0) {
      return <>{children}</>;
    }

    if (user && permissions.includes(user.role)) {
      return <>{children}</>;
    }

    return fallback;
  },
);

// This component can be used as a parent route with nested routes
export const ProtectedRoute = observer(
  ({
    permissions,
    fallback = <Navigate to="/unauthorized" />,
  }: Omit<RequireAuthProps, 'children'>) => {
    const { user } = useAuth();

    if (!permissions || permissions?.length === 0) {
      return <Outlet />;
    }

    if (user && permissions.includes(user.role)) {
      return <Outlet />;
    }

    return fallback;
  },
);
